import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { subscriptionService } from '../../services/subscriptionService'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { CreditCard, AlertTriangle } from '../icons/Icons'
import s from './ReactivationFlow.module.css'

const BILLING_EMAIL = 'billingiq@allocatiq.com'

interface ReactivationFlowProps {
  isOpen: boolean
  onClose: () => void
}

export function ReactivationFlow({ isOpen, onClose }: ReactivationFlowProps) {
  const { organization } = useAuth()
  const { subscription, lifecycleState, gracePeriodDaysLeft, refresh } = useSubscription()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleContactBilling = () => {
    window.location.href = `mailto:${BILLING_EMAIL}?subject=Reactivate Subscription - ${organization?.name}&body=Hello,%0D%0A%0D%0AI would like to reactivate my subscription.%0D%0A%0D%0AOrganization: ${organization?.name}%0D%0AOrganization ID: ${organization?.id}%0D%0A%0D%0AThank you`
  }

  const getMessage = () => {
    if (lifecycleState === 'grace_period') {
      return {
        title: 'Your Account is in Grace Period',
        description: `You have ${gracePeriodDaysLeft} day${gracePeriodDaysLeft === 1 ? '' : 's'} remaining to reactivate your subscription before your account is suspended and access is restricted to read-only mode.`,
        icon: <AlertTriangle size={48} style={{ color: '#d13438' }} />
      }
    }

    if (lifecycleState === 'suspended') {
      return {
        title: 'Your Account is Suspended',
        description: 'Your account has been suspended due to non-payment. All data is preserved in read-only mode. Reactivate your subscription to regain full access.',
        icon: <AlertTriangle size={48} style={{ color: '#d13438' }} />
      }
    }

    return {
      title: 'Reactivate Your Subscription',
      description: 'Restore full access to your account by reactivating your subscription.',
      icon: <CreditCard size={48} style={{ color: '#0078d4' }} />
    }
  }

  const message = getMessage()

  if (success) {
    return (
      <Modal open={isOpen} onClose={onClose} title="Reactivation Request Sent">
        <div className={s.successContainer}>
          <div className={s.successIcon}>✓</div>
          <h3 className={s.successTitle}>We've received your request</h3>
          <p className={s.successMessage}>
            Our billing team will contact you shortly to complete the reactivation process.
            Check your email for further instructions.
          </p>
          <Button onClick={onClose}>Close</Button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal open={isOpen} onClose={onClose} title="Reactivate Subscription">
      <div className={s.container}>
        <div className={s.iconWrapper}>
          {message.icon}
        </div>

        <h2 className={s.title}>{message.title}</h2>
        <p className={s.description}>{message.description}</p>

        {lifecycleState === 'grace_period' && (
          <div className={s.warningBox}>
            <AlertTriangle size={16} />
            <div>
              <strong>Time-Sensitive:</strong> Your account will be suspended in {gracePeriodDaysLeft} day{gracePeriodDaysLeft === 1 ? '' : 's'}.
              After suspension, you'll have read-only access until reactivation.
            </div>
          </div>
        )}

        {lifecycleState === 'suspended' && (
          <div className={s.infoBox}>
            <strong>Your data is safe:</strong> All your projects, resources, and allocations are preserved.
            Once you reactivate, you'll have immediate full access to everything.
          </div>
        )}

        <div className={s.planInfo}>
          <h3 className={s.planTitle}>Reactivation Details</h3>
          <div className={s.planRow}>
            <span>Current Plan:</span>
            <strong>{subscription?.tier.charAt(0).toUpperCase()}{subscription?.tier.slice(1)}</strong>
          </div>
          <div className={s.planRow}>
            <span>Organization:</span>
            <strong>{organization?.name}</strong>
          </div>
        </div>

        {error && (
          <div className={s.errorBox}>
            {error}
          </div>
        )}

        <div className={s.actions}>
          <Button
            variant="primary"
            onClick={handleContactBilling}
            style={{ width: '100%' }}
          >
            Contact Billing Team
          </Button>
          <p className={s.helpText}>
            Email our billing team at <a href={`mailto:${BILLING_EMAIL}`}>{BILLING_EMAIL}</a> to
            process your payment and reactivate your account immediately.
          </p>
        </div>

        <div className={s.footer}>
          <p className={s.footerText}>
            Questions? Our team is here to help you get back up and running quickly.
          </p>
        </div>
      </div>
    </Modal>
  )
}
